import React from 'react';
import { motion } from 'framer-motion';

const StyledButton = ({ 
  children, 
  variant = 'primary', 
  size = 'md', 
  icon: Icon, 
  loading = false, 
  disabled = false, 
  className = '', 
  onClick, 
  type = 'button',
  fullWidth = false,
  ...props 
}) => {
  const baseClasses = "group relative overflow-hidden rounded-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none";
  
  const variants = {
    primary: "bg-gradient-to-r from-iranian-400 to-iranian-600 p-0.5 hover:from-iranian-500 hover:to-iranian-700 hover:shadow-iranian hover:shadow-xl",
    secondary: "bg-gradient-to-r from-gray-700 to-gray-800 p-0.5 hover:from-gray-600 hover:to-gray-700 hover:shadow-lg hover:shadow-gray-500/25",
    success: "bg-gradient-to-r from-green-500 to-green-600 p-0.5 hover:from-green-600 hover:to-green-700 hover:shadow-lg hover:shadow-green-500/25",
    danger: "bg-gradient-to-r from-red-500 to-red-600 p-0.5 hover:from-red-600 hover:to-red-700 hover:shadow-lg hover:shadow-red-500/25",
    warning: "bg-gradient-to-r from-yellow-500 to-yellow-600 p-0.5 hover:from-yellow-600 hover:to-yellow-700 hover:shadow-lg hover:shadow-yellow-500/25",
    outline: "bg-gradient-to-r from-transparent to-transparent p-0.5 border-2 border-iranian-500 hover:border-iranian-400"
  };

  const sizes = {
    sm: "px-4 py-2 text-sm",
    md: "px-6 py-3 text-base",
    lg: "px-8 py-4 text-lg",
    xl: "px-10 py-5 text-xl"
  };

  const innerVariants = {
    primary: "bg-gradient-to-r from-iranian-500 to-iranian-600 text-white",
    secondary: "bg-gradient-to-r from-gray-800 to-gray-900 text-gray-300 group-hover:text-white",
    success: "bg-gradient-to-r from-green-600 to-green-700 text-white",
    danger: "bg-gradient-to-r from-red-600 to-red-700 text-white",
    warning: "bg-gradient-to-r from-yellow-600 to-yellow-700 text-gray-900",
    outline: "bg-transparent text-iranian-400 group-hover:text-iranian-300"
  };

  const widthClass = fullWidth ? "w-full" : "";

  return (
    <motion.button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`${baseClasses} ${variants[variant]} ${widthClass} ${className}`}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      {...props}
    >
      <div className={`relative flex items-center justify-center gap-3 rounded-[10px] ${innerVariants[variant]} ${sizes[size]} transition-all duration-300`}>
        {/* Overlay effect */}
        <div className={`absolute inset-0 rounded-[10px] transition-opacity duration-300 group-hover:opacity-100 ${
          variant === 'primary' || variant === 'success' || variant === 'danger' || variant === 'warning'
            ? 'bg-gradient-to-r from-white/20 to-white/10 opacity-0'
            : 'bg-gradient-to-r from-iranian-400/20 to-iranian-600/20 opacity-0'
        }`}></div>
        
        {/* Content */}
        <div className="relative z-10 flex items-center gap-3">
          {loading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-current"></div>
          ) : (
            <>
              {Icon && (
                <Icon 
                  size={size === 'sm' ? 16 : size === 'lg' ? 20 : size === 'xl' ? 24 : 18} 
                  className="transition-transform duration-300 group-hover:scale-110" 
                />
              )}
              <span className="font-semibold">{children}</span>
            </>
          )}
        </div>

        {/* Shine effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 opacity-0 transition-opacity duration-500 group-hover:opacity-100 rounded-[10px] animate-shine"></div>
      </div>
    </motion.button>
  );
};

export default StyledButton;
